import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Flex, Text, useColorModeValue } from '@bonusly/design-system';

import { Bizy } from '../../bizy';
import type { WhatsNewTip } from '../hooks/use-admin-whats-new-tip';

interface BizyAdminWhatsNewTipProps {
	tip: WhatsNewTip;
	onSelectPrompt: (prompt: string) => void;
}

export const BizyAdminWhatsNewTip: FC<BizyAdminWhatsNewTipProps> = ({ tip, onSelectPrompt }) => {
	const { t } = useTranslation('admin_bizy');

	const promptText = t(tip.promptKey);

	// Light mode keeps the Figma-spec pale blue → pale lavender halo. Dark mode reuses
	// the same tonal gray-blue alpha that sits behind the chat panel (see PANEL_BG in
	// `bizy-side-panel.tsx`) — same color, just bumped alpha so the halo reads as a
	// shape behind Bizy without glaring on the dark surface.
	const haloBg = useColorModeValue(
		'linear-gradient(180deg, #E8F4FF 0%, #F5E6FF 100%)',
		'linear-gradient(180deg, rgba(107, 116, 133, 0.08) 0%, rgba(107, 116, 133, 0.18) 100%)'
	);

	// Light mode fills the prompt button with the surface color; dark mode lets the
	// panel's tonal gradient show through, with a subtle whiteAlpha hover tint.
	const promptBg = useColorModeValue('bg.surface', 'transparent');
	const promptHoverBg = useColorModeValue('bg.canvas', 'whiteAlpha.100');

	const handleClick = () => {
		onSelectPrompt(promptText);
	};

	return (
		<Flex direction="column" align="center" gap={6} p={6} borderRadius="16px" w="100%" maxW="300px" mx="auto">
			<Box position="relative" w="120px" h="120px" borderRadius="full" bg={haloBg} display="flex" alignItems="center" justifyContent="center">
				<Bizy state={tip.state} size={80} />
			</Box>

			<Flex direction="column" align="center" gap={2}>
				<Text fontSize="sm" lineHeight="20px" color="text.soft">
					{t('whatsNew.label')}
				</Text>
				<Text fontSize="lg" lineHeight="24px" fontWeight="semibold" color="text.default" textAlign="center">
					{t(tip.headlineKey)}
				</Text>
			</Flex>

			<Flex direction="column" align="stretch" w="100%" gap={2}>
				<Text fontSize="sm" color="text.soft" textAlign="center">
					{t('whatsNew.tryItOut')}
				</Text>
				<Box
					as="button"
					onClick={handleClick}
					p={3}
					borderRadius="12px"
					border="1px solid"
					borderColor="transparent"
					bg={promptBg}
					position="relative"
					cursor="pointer"
					transition="all 0.2s ease"
					_hover={{
						bg: promptHoverBg,
					}}
					// Gradient border colors are fixed per Figma design spec
					_before={{
						content: '""',
						position: 'absolute',
						inset: '-1px',
						borderRadius: '13px',
						padding: '1px',
						background: 'linear-gradient(135deg, #C5E8BA 0%, #70C481 25%, #B3EBE8 50%, #9783EA 75%, #C5E8BA 100%)',
						WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
						WebkitMaskComposite: 'xor',
						maskComposite: 'exclude',
						pointerEvents: 'none',
					}}
				>
					<Text fontSize="sm" color="text.softest" textAlign="center">
						{promptText}
					</Text>
				</Box>
			</Flex>
		</Flex>
	);
};

BizyAdminWhatsNewTip.displayName = 'BizyAdminWhatsNewTip';
